import React, { useState, useEffect, useMemo } from 'react';
import { useSecretaryStore } from '../store/secretaryStore';
import { X, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, Info } from 'lucide-react';
import { fetchDisponibilidadMensual, fetchConfigsForProfessionals, BloqueOcupado } from '../services/CalendarService';
import type { AgendaConfig } from '../store/secretaryStore';

const DIAS_SEMANA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export function BuscadorInteligenteModal() {
  const { 
    isBuscadorInteligenteOpen, 
    setBuscadorInteligenteOpen, 
    activeDoctorId, 
    workspaceProfesionales,
    configProfesionales,
    setNuevaCitaOpen,
    setSelectedDate,
    setSelectedTimeSlot
  } = useSecretaryStore();

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [bloquesOcupados, setBloquesOcupados] = useState<BloqueOcupado[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  // Fallback local: se usa cuando CalendarGrid aún no ha poblado el store
  const [localConfig, setLocalConfig] = useState<AgendaConfig | null>(null);

  // Determinar qué profesional estamos consultando
  const selectedProfesional = activeDoctorId || (workspaceProfesionales?.length > 0 ? workspaceProfesionales[0].uuid : null);
  // Usamos la config del store si existe, sino la del fallback local que cargamos nosotros mismos
  const configProf = selectedProfesional
    ? (configProfesionales[selectedProfesional] ?? localConfig)
    : null;

  // Cargar disponibilidad en Bulk (Payload Trimming)
  useEffect(() => {
    if (!isBuscadorInteligenteOpen || !selectedProfesional) return;

    let isMounted = true;

    const loadMonthData = async () => {
      setIsLoading(true);
      
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      
      // Pedimos desde el día 1 hasta el último día del mes (en formato ISO local aproximado)
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0, 23, 59, 59);
      
      const inicioIso = firstDay.toISOString();
      const finIso = lastDay.toISOString();

      try {
        // Carga paralela: bloques ocupados + config (si el store no la tiene aún)
        const needsConfig = !configProfesionales[selectedProfesional];

        const promises = [
          fetchDisponibilidadMensual(selectedProfesional, inicioIso, finIso),
          needsConfig
            ? fetchConfigsForProfessionals([selectedProfesional])
            : Promise.resolve(null),
        ] as const;

        const [bloques, extraConfigs] = await Promise.all(promises);

        if (isMounted) {
          setBloquesOcupados(bloques);
          setSelectedDay(null);
          if (extraConfigs?.[selectedProfesional]) {
            setLocalConfig(extraConfigs[selectedProfesional]);
            console.log('[BuscadorInteligente] ✅ localConfig SET from fetch');
          } else if (needsConfig) {
            console.warn('[BuscadorInteligente] ⚠️ Config fetch returned EMPTY — probable RLS block or missing row in profesional_config_agenda');
          }
        }
      } catch (err) {
        console.error("Error cargando bloques", err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadMonthData();

    return () => { isMounted = false; };
  }, [currentMonth, selectedProfesional, isBuscadorInteligenteOpen]);

  // Generar la matriz del calendario
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  }, [currentMonth]);

  // Calcular huecos para un día específico
  const getHuecosLibresParaDia = (day: number) => {
    if (!configProf) return [];
    
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const dateObj = new Date(year, month, day);
    
    // Mapeo: getDay() retorna 0=Dom...6=Sáb, pero el JSONB usa nombres en español
    const DIAS_MAP = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
    const dayOfWeek = dateObj.getDay();
    const configDia = configProf.dias[DIAS_MAP[dayOfWeek]];
    
    if (!configDia || !configDia.activo || !configDia.intervalos || configDia.intervalos.length === 0) {
      return [];
    }

    const duracion = configProf.duracion_minutos || 45;
    const brecha = configProf.brecha_minutos || 0;
    const huecos: { inicio: string, fin: string, dateObj: Date }[] = [];

    // Generar posibles slots teóricos
    for (const intervalo of configDia.intervalos) {
      if (!intervalo.inicio || !intervalo.fin) continue;
      
      const [startH, startM] = intervalo.inicio.split(':').map(Number);
      const [endH, endM] = intervalo.fin.split(':').map(Number);
      
      let slotStart = new Date(year, month, day, startH, startM, 0);
      const intervalEnd = new Date(year, month, day, endH, endM, 0);

      while (true) {
        const slotEnd = new Date(slotStart.getTime() + duracion * 60000);
        
        if (slotEnd > intervalEnd) break;

        huecos.push({
          inicio: `${String(slotStart.getHours()).padStart(2, '0')}:${String(slotStart.getMinutes()).padStart(2, '0')}`,
          fin: `${String(slotEnd.getHours()).padStart(2, '0')}:${String(slotEnd.getMinutes()).padStart(2, '0')}`,
          dateObj: slotStart
        });

        // Siguiente slot
        slotStart = new Date(slotEnd.getTime() + brecha * 60000);
      }
    }

    // Filtrar slots ocupados (cruzando con bloquesOcupados)
    const huecosLibres = huecos.filter(hueco => {
      const huecoStartTime = hueco.dateObj.getTime();
      const huecoEndTime = huecoStartTime + duracion * 60000;

      // Un hueco es libre si NO se solapa con NINGÚN bloque ocupado
      const solapa = bloquesOcupados.some(b => {
        const bStart = new Date(b.inicioIso).getTime();
        const bEnd = new Date(b.finIso).getTime();
        
        const intersect = huecoStartTime < bEnd && huecoEndTime > bStart;
        return intersect;
      });

      // También filtramos si el hueco ya pasó en el tiempo (hoy)
      const now = Date.now();
      if (huecoStartTime < now) return false;

      return !solapa;
    });

    return huecosLibres;
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const isToday = (day: number) => {
    const today = new Date();
    return today.getDate() === day && today.getMonth() === currentMonth.getMonth() && today.getFullYear() === currentMonth.getFullYear();
  };
  
  const isPast = (day: number) => {
    const today = new Date();
    today.setHours(0,0,0,0);
    const cellDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return cellDate < today;
  };

  const handleAgendar = (day: number, slot: { inicio: string, fin: string }) => {
    const year = currentMonth.getFullYear();
    const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    
    setSelectedDate(`${year}-${month}-${dd}`);
    setSelectedTimeSlot(slot);
    setBuscadorInteligenteOpen(false);
    setNuevaCitaOpen(true);
  };

  if (!isBuscadorInteligenteOpen) return null;

  return (
    <>
      <div 
        aria-hidden="true"
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-110 animate-in fade-in duration-200"
        onClick={() => setBuscadorInteligenteOpen(false)}
      />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-white dark:bg-slate-900 shadow-2xl rounded-3xl z-111 overflow-hidden flex flex-col animate-in zoom-in-95 duration-200 border border-slate-200 dark:border-slate-800">
        
        {/* Header */}
        <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-white dark:bg-slate-900 shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 rounded-xl flex items-center justify-center">
              <CalendarIcon className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-black text-slate-800 dark:text-white leading-tight">Buscador Inteligente</h2>
              <p className="text-xs font-medium text-slate-500">Encuentra espacios rápidamente</p>
            </div>
          </div>
          <button 
            onClick={() => setBuscadorInteligenteOpen(false)}
            className="p-2 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-full text-slate-500 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-6 bg-slate-50/50 dark:bg-slate-900">
          
          {/* Controls */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-black text-slate-800 dark:text-slate-100 capitalize">
              {MESES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h3>
            <div className="flex items-center gap-2">
              <button onClick={prevMonth} className="p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 transition-colors text-slate-600 dark:text-slate-300">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button onClick={nextMonth} className="p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 transition-colors text-slate-600 dark:text-slate-300">
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>

          {!configProf && (
            <div className="p-4 bg-slate-100 dark:bg-slate-800/50 rounded-2xl flex items-start gap-3 mb-6 shadow-inner">
              <Info className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
              <p className="text-sm text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
                El profesional seleccionado aún no ha definido un horario base de atención. Por ahora, el buscador asumirá que su agenda está cerrada.
              </p>
            </div>
          )}

          {/* Grid */}
          <div className="relative">
            {isLoading && (
              <div className="absolute inset-0 bg-white/60 dark:bg-slate-900/60 backdrop-blur-[2px] z-10 flex items-center justify-center rounded-2xl">
                <div className="h-8 w-8 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
              </div>
            )}
            
            <div className="grid grid-cols-7 gap-2 mb-2">
              {DIAS_SEMANA.map(d => (
                <div key={d} className="text-center text-xs font-bold text-slate-400 uppercase tracking-wider py-1">
                  {d}
                </div>
              ))}
            </div>
            
            <div className="grid grid-cols-7 gap-2">
              {calendarDays.map((day, idx) => {
                if (day === null) {
                  return <div key={`empty-cell-${idx.toString()}`} className="h-12 rounded-xl" />;
                }
                
                const past = isPast(day);
                const huecosLibres = past ? [] : getHuecosLibresParaDia(day);
                const totalHuecos = huecosLibres.length;
                
                let bgClass: string;
                let textClass: string;
                
                if (!past) {
                  if (totalHuecos > 5) {
                    bgClass = "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 hover:border-emerald-400 dark:hover:border-emerald-600 cursor-pointer";
                    textClass = "text-emerald-700 dark:text-emerald-400 font-bold";
                  } else if (totalHuecos > 0) {
                    bgClass = "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 hover:border-amber-400 dark:hover:border-amber-600 cursor-pointer";
                    textClass = "text-amber-700 dark:text-amber-400 font-bold";
                  } else {
                    bgClass = "bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-800 cursor-not-allowed opacity-60";
                    textClass = "text-slate-400 dark:text-slate-600";
                  }
                } else {
                  bgClass = "bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-800 opacity-40 cursor-not-allowed";
                  textClass = "text-slate-400 dark:text-slate-500";
                }

                const today = isToday(day);

                return (
                  <button
                    key={day}
                    disabled={past || totalHuecos === 0}
                    onClick={() => setSelectedDay(day === selectedDay ? null : day)}
                    className={`
                      relative h-14 rounded-xl border flex flex-col items-center justify-center transition-all duration-300 ease-out
                      ${bgClass}
                      ${selectedDay === day ? 'ring-2 ring-purple-500 ring-offset-2 ring-offset-white dark:ring-offset-slate-900 scale-[1.03] z-10 shadow-lg' : ''}
                    `}
                  >
                    <span className={`text-sm ${textClass}`}>{day}</span>
                    
                    {!past && totalHuecos > 0 && (
                      <span className={`text-[9px] mt-0.5 font-bold px-1.5 py-0.5 rounded-md border ${
                        totalHuecos > 5 
                          ? 'bg-emerald-100 border-emerald-200 text-emerald-700 dark:bg-emerald-900/50 dark:border-emerald-700 dark:text-emerald-300' 
                          : 'bg-amber-100 border-amber-200 text-amber-700 dark:bg-amber-900/50 dark:border-amber-700 dark:text-amber-300'
                      }`}>
                        {totalHuecos} citas
                      </span>
                    )}

                    {!!today && totalHuecos === 0 && (
                      <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-blue-500" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Expanded View for Selected Day */}
          {!!selectedDay && (
            <div className="mt-6 p-5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl animate-in fade-in slide-in-from-bottom-4 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                <h3 className="font-bold text-slate-800 dark:text-slate-100">
                  Huecos libres el {selectedDay} de {MESES[currentMonth.getMonth()]}
                </h3>
              </div>
              
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {getHuecosLibresParaDia(selectedDay).map((slot) => (
                  <button
                    key={`${selectedDay}-${slot.inicio}`}
                    onClick={() => handleAgendar(selectedDay, slot)}
                    className="py-2.5 px-3 bg-purple-50 hover:bg-purple-600 dark:bg-purple-900/30 dark:hover:bg-purple-600 text-purple-700 hover:text-white dark:text-purple-300 dark:hover:text-white rounded-xl text-sm font-bold transition-colors border border-purple-100 hover:border-purple-600 dark:border-purple-800 shadow-sm"
                  >
                    {slot.inicio}
                  </button>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}
